"use client";

import Link from "next/link";
import { m } from "framer-motion";
import { categoryBrowseHref, categoryData, categoryNameToSlug } from "@/lib/category-data";
import SoundCard from "@/components/ui/sound-card";
import type { Sound } from "@/lib/supabase";
import { itemVariants } from "@/components/sound-player/shared-motion";

// DS SoundDetail template: "related" is a hairline list of density-2 rows
// (56px) on the column axis, four of them; not a carousel, not a
// horizontal scroller. Rows beyond the cap live behind the "All ... sounds"
// link, which is the one navigation affordance this block needs.
const RELATED_ROW_CAP = 4;

export interface RelatedSoundsRailProps {
  sound: Sound;
  relatedSounds: Sound[];
  /** Pre-resolved related-rail heading (TES-810b waterfall). */
  relatedRailTitle?: string;
}

/**
 * Related sounds list (density-2 hairline rows) extracted from
 * sound-player-client.tsx.
 *
 * NOTE: the root element is an `m.div` with the page's stagger item variants,
 * so this component MUST be rendered inside a `<LazyMotion>` ancestor (the
 * sound player page wraps everything in `<LazyMotion features={domAnimation}>`).
 * Renders null when `relatedSounds` is empty.
 */
export function RelatedSoundsRail({
  sound,
  relatedSounds,
  relatedRailTitle,
}: RelatedSoundsRailProps) {
  const catSlug = categoryNameToSlug(sound.category);
  const catInfo = categoryData[catSlug];

  // Subcollection match for related rail label (§5.4).
  const matchedSubcollection = catInfo?.subcollections?.find(sc =>
    sc.keywords.some(kw => {
      const kwLower = kw.toLowerCase();
      return (
        sound.title.toLowerCase().includes(kwLower) ||
        sound.tags?.some(t => t.toLowerCase().includes(kwLower))
      );
    })
  );

  if (relatedSounds.length === 0) return null;

  return (
    <m.div variants={itemVariants}>
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
        <h2 className="tls-h2 m-0 text-balance">
          {relatedRailTitle
            ? relatedRailTitle
            : matchedSubcollection
              ? `More ${matchedSubcollection.label} sounds`
              : "Related sounds"}
        </h2>
        {/* Same treatment as the homepage section index links: ink-muted at
            rest, accent on hover. */}
        <Link
          href={categoryBrowseHref(sound.category)}
          className="tlsr-focus tls-touch-pad whitespace-nowrap text-sm font-semibold text-[var(--tls-fg-muted)] no-underline transition-colors hover:text-[color:var(--tls-brand-ink)]"
        >
          All {sound.category} sounds
        </Link>
      </div>

      {/* Plain hairline list on the column axis: no chevrons, no indent, the
          hairline spans exactly the content column. */}
      <div
        className="grid grid-cols-1 gap-0"
        style={{ borderTop: "1px solid var(--tls-border-subtle)" }}
      >
        {relatedSounds.slice(0, RELATED_ROW_CAP).map(relatedSound => (
          <SoundCard
            key={relatedSound.id}
            sound={relatedSound}
            showCategory={false}
            density="d2"
            related
            disableEntryAnimation
          />
        ))}
      </div>
    </m.div>
  );
}
